// src/ui/components/debug/DebugUtils.ts

/**
 * Utilidades compartidas para debugging
 */

export interface DebugLogEntry {
  timestamp: number;
  category: string;
  message: string;
  data?: any;
  resolution?: {
    width: number;
    height: number;
    textScale: number;
    category: string;
  };
}

const debugLogs: DebugLogEntry[] = [];

/**
 * Verifica si el modo debug está activo
 */
export const isDebugMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Verificar parámetro de URL
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('debug') === 'true' || urlParams.get('debug') === 'autofit') {
    return true;
  }
  
  // Verificar variable de entorno
  if (process.env.REACT_APP_DEBUG_AUTOFIT === 'true') {
    return true;
  }
  
  // En desarrollo, siempre activo
  return process.env.NODE_ENV === 'development';
};

/**
 * Verifica si el debug específico de autofit está activo
 */
export const isAutoFitDebugActive = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('debug') === 'autofit') {
    return true;
  }
  
  if (process.env.REACT_APP_DEBUG_AUTOFIT === 'true') {
    return true;
  }
  
  return process.env.NODE_ENV === 'development';
};

/**
 * Agrega un log de debug
 */
export const addDebugLog = (
  category: string,
  message: string,
  data?: any,
  resolution?: {
    width: number;
    height: number;
    textScale: number;
    category: string;
  }
): void => {
  if (!isDebugMode()) return;
  
  debugLogs.push({
    timestamp: Date.now(),
    category,
    message,
    data,
    resolution,
  });
  
  // También loggear a consola si está activo
  if (isAutoFitDebugActive()) {
    console.log(`[DEBUG ${category}]`, message, data || '');
  }
};

/**
 * Obtiene todos los logs de debug
 */
export const getDebugLogs = (): DebugLogEntry[] => {
  return [...debugLogs];
};

/**
 * Limpia todos los logs
 */
export const clearDebugLogs = (): void => {
  debugLogs.length = 0;
};

/**
 * Exporta los logs como JSON
 */
export const exportDebugLogs = (): string => {
  const exportData = {
    timestamp: new Date().toISOString(),
    resolution: typeof window !== 'undefined' ? {
      width: window.innerWidth,
      height: window.innerHeight,
    } : null,
    logs: debugLogs,
  };
  
  return JSON.stringify(exportData, null, 2);
};

/**
 * Descarga los logs como archivo
 */
export const downloadDebugLogs = (): void => {
  const json = exportDebugLogs();
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `debug-logs-${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

